/**
 * Die offenen Mengen an den Aufgabenkacheln der Startseite — aus dem Bestand,
 * nicht aus dem Quelltext.
 *
 * Bis zum 13.08.2026 standen sie als letztes Feld im Katalogeintrag: `12` an
 * den Änderungsfreigaben, `2` an der Rezertifizierung, `7` an den
 * SoD-Konflikten. Übernommen aus der HTML-Vorlage, an nichts gebunden. Die
 * Sieben ließ sich widerlegen, sobald `/sod` aus dem Bestand rechnete — dort
 * sind es im Entwicklungsbestand 63. Die anderen beiden waren nicht besser,
 * nur schwerer zu prüfen.
 *
 * Dieselbe Regel wie in `api/zaehler.ts`, und sie ist der Grund für die
 * Umständlichkeit hier: **keine Zahl anzeigen, solange nichts geladen ist.**
 * Ein `?? 0` wäre bequem und wäre eine Aussage über den Bestand — „hier liegt
 * nichts" —, die niemand geprüft hat. Deshalb unterscheidet `Aufgabenstand`
 * vier Lagen statt einer Zahl mit Ersatzwert.
 *
 * ## Was gezählt wird
 *
 * | Schlüssel | Quelle | Menge |
 * |---|---|---|
 * | `freigaben` | `/api/freigaben?zustand=eingereicht` | offene Änderungsfreigaben |
 * | `rezert` | `/api/rezertifizierung/positionen?zustand=offen&meine=ja` | eigene offene Positionen |
 * | `sod` | `/api/sod/befunde` | Befunde aller aktiven Regeln |
 * | `kontenWarten` | `/api/konten?ohneRolle=ja` | Konten ohne wirksame Anwendungsrolle |
 *
 * Zwei Einschränkungen, die die Zahlen ehrlich halten:
 *
 * 1. **`freigaben` zählt offene, nicht entscheidbare.** Ob jemand entscheiden
 *    darf, steht je Zeile in `darfEntscheiden` — wer einreicht, entscheidet
 *    nicht. Danach zu filtern hieße, die Zeilen zu holen und im Browser zu
 *    zählen; das wäre bei einem echten Bestand eine Zählung über die höchstens
 *    200 Zeilen eines Abrufs. Es ist dieselbe Zahl, die der Reiter „Offen"
 *    unter `/genehmigungen` zeigt.
 * 2. **`sod` überträgt die Befunde, um sie zu zählen.** `/api/sod/befunde`
 *    blättert nicht; die Antwort trägt alle Zeilen. Für den
 *    Entwicklungsbestand (63) ist das folgenlos, für einen echten nicht — die
 *    Schnittstelle braucht dafür einen Kopfzähler. Vermerkt statt verschwiegen.
 *
 * 3. **`kontenWarten` zählt über die Sicht `iam.vwKontoOhneRolle`, nicht über
 *    `Freigabezustand`.** Ein Konto kann freigegeben und trotzdem ohne Rolle
 *    sein — dann sieht die Person genauso wenig, und die Zahl soll das sagen.
 *    Umgekehrt gilt: wer eine Rolle bekommen hat, verschwindet aus der Liste,
 *    auch wenn niemand den Zustand nachgepflegt hat.
 *
 * Gezählt wird nur, was der Mandant führt und der Angemeldete sehen darf.
 * Route und Recht stehen dafür nicht ein zweites Mal hier, sondern werden aus
 * dem Katalogeintrag gelesen, der den Schlüssel trägt: ein zweiter Katalog
 * wäre ein zweiter Ort zum Vergessen.
 */

import { useMemo } from 'react';
import { useListe, type Zustand } from './haken.js';
import { useSodBefunde } from './sod.js';
import { usePositionen } from './rezertifizierung.js';
import { KATALOG, type Kachel, type Zaehlerschluessel } from '../entwurf/katalog.js';
import { routeErlaubt, type Profil } from '../entwurf/navigation.js';
import type { Seite } from './klient.js';

/**
 * Der Stand eines Zählers.
 *
 * `aus` und `fehler` sind bewusst zwei Fälle: „gibt es für Sie nicht" und
 * „konnte nicht geladen werden" führen zwar beide dazu, dass keine Zahl
 * erscheint, aber nur der zweite gehört gemeldet.
 */
export type Aufgabenstand =
  /** Der Mandant führt das Modul nicht, oder das Recht fehlt. Es wird nichts abgerufen. */
  | { art: 'aus' }
  /** Der Abruf läuft. Bis er antwortet, steht dort keine Zahl. */
  | { art: 'laedt' }
  | { art: 'zahl'; zahl: number }
  | { art: 'fehler'; text: string; erneut: () => void };

const AUS: Aufgabenstand = { art: 'aus' };

/** Die Katalogeinträge nach Zählerschlüssel — einmal aufgebaut, nicht je Rendervorgang. */
const EINTRAEGE: Map<Zaehlerschluessel, Kachel> = new Map(
  KATALOG.flatMap(([, kacheln]) => kacheln)
    .flatMap((k) => (k[6] === null ? [] : [[k[6], k] as const])),
);

/**
 * Route und Recht des Eintrags gegen Modulzuschnitt und Rechte prüfen.
 *
 * `darfDas` kommt aus `App.tsx` und liest `/api/ich`. Bis zum 27.08.2026
 * stand hier `darf(rollen, …)` gegen die im Browser gewählte Persona: ein
 * Zähler ging damit **nicht** hinaus, weil der Benutzer sich eine Rolle
 * ohne das Recht gewählt hatte — und ging hinaus und endete in 403, wenn
 * er sich eine mit dem Recht gewählt hatte, das sein Konto nicht trägt.
 *
 * Exportiert, weil die Glocke (`api/meldungen.ts`) dieselbe Frage stellt: sie
 * zeigt dieselben Mengen, nur an anderer Stelle. Zwei Listen von Route und
 * Recht wären zwei Orte zum Vergessen.
 */
export function zaehlerGefragt(
  schluessel: Zaehlerschluessel,
  profil: Profil,
  darfDas: (recht: string | null) => boolean,
): boolean {
  const k = EINTRAEGE.get(schluessel);
  return k !== undefined && routeErlaubt(k[4], profil) && darfDas(k[5]);
}

/**
 * Einen Abruf in einen Stand übersetzen.
 *
 * Die Reihenfolge ist wesentlich: erst der Fehler, dann die Daten, sonst nichts.
 * Wer `daten` zuerst prüft, zeigt nach einem gescheiterten Nachladen weiter den
 * alten Wert und meldet den Ausfall nie.
 */
function stand(aktiv: boolean, abruf: Zustand<Seite<unknown>>): Aufgabenstand {
  if (!aktiv) return AUS;
  if (abruf.fehler !== null) return { art: 'fehler', text: abruf.fehler, erneut: abruf.erneut };
  if (abruf.daten) return { art: 'zahl', zahl: abruf.daten.gesamt };
  return { art: 'laedt' };
}

/**
 * Die Zähler aller Aufgabenkacheln.
 *
 * Ein Abruf je Schlüssel. Abgeschaltete gehen nicht hinaus: eine Anfrage, die
 * absehbar mit 403 oder 404 endet, soll erst gar nicht gestellt werden.
 */
export function useAufgabenzaehler(
  mandant: string,
  profil: Profil,
  darfDas: (recht: string | null) => boolean,
): Record<Zaehlerschluessel, Aufgabenstand> {
  const freigabenAn = zaehlerGefragt('freigaben', profil, darfDas);
  const rezertAn = zaehlerGefragt('rezert', profil, darfDas);
  const sodAn = zaehlerGefragt('sod', profil, darfDas);
  const kontenAn = zaehlerGefragt('kontenWarten', profil, darfDas);

  // `groesse: 1` — gebraucht wird der Kopfzähler `gesamt`, nicht die Zeilen.
  const freigaben = useListe<unknown>('freigaben', mandant, {
    seite: 1, groesse: 1, filter: { zustand: 'eingereicht' }, aus: !freigabenAn,
  });
  const rezert = usePositionen(mandant, {
    zustand: 'offen', meine: true, seite: 1, groesse: 1, aus: !rezertAn,
  });
  const sod = useSodBefunde(mandant, { aus: !sodAn });
  const kontenWarten = useListe<unknown>('konten', mandant, {
    seite: 1, groesse: 1, filter: { ohneRolle: 'ja' }, aus: !kontenAn,
  });

  return useMemo(() => ({
    freigaben: stand(freigabenAn, freigaben),
    rezert: stand(rezertAn, rezert),
    sod: stand(sodAn, sod),
    kontenWarten: stand(kontenAn, kontenWarten),
  }), [
    freigabenAn, freigaben.daten, freigaben.fehler, freigaben.erneut,
    rezertAn, rezert.daten, rezert.fehler, rezert.erneut,
    sodAn, sod.daten, sod.fehler, sod.erneut,
    kontenAn, kontenWarten.daten, kontenWarten.fehler, kontenWarten.erneut,
  ]);
}
